import React, { useMemo } from 'react';

import formatInTimeZone from 'date-fns-tz/formatInTimeZone';
import { useIntl } from 'react-intl-next';
import { usePathParam } from 'react-resource-router';

import DynamicTable from '@atlaskit/dynamic-table';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';

import { MIN_DATE_SHOWN_EPOCH, SPOTLIGHT_NAMES } from '../../common/constants';
import { DeleteModal } from '../../common/ui/delete-modal';
import { HeartbeatStatus } from '../../common/ui/status';
import { SwitchModal } from '../../common/ui/switch-modal';
import { getMaxAvailableEpoch } from '../../common/utils/helpers';
import { useDeleteModalController } from '../../controllers/delete-modal';
import { useListController } from '../../controllers/list';
import { useHeartbeatsContext } from '../../controllers/providers/heartbeats';
import { useSwitchController } from '../../controllers/switch';

import { Action } from './actions';
import messages from './messages';
import { NoSearchResultEmptyState } from './no-search-result-empty-state';
import { DynamicTableWrapper } from './styled';
import { Team } from './team';

export const List = () => {
	const { formatMessage } = useIntl();
	const { heartbeats, timezone } = useListController();
	const { focusState, isTransitioning } = useHeartbeatsContext();
	const [teamId] = usePathParam('teamId');
	const {
		isDeleteModalOpen,
		handleDelete,
		handleDeleteClick,
		isDeleting,
		handleModalClose: handleDeleteModalClose,
	} = useDeleteModalController();
	const {
		handleSwitchClick,
		handleSwitch,
		isSwitchModalOpen,
		isSwitchLoading,
		handleModalClose: handleSwitchModalClose,
	} = useSwitchController();

	const head = useMemo(
		() => ({
			cells: [
				{
					key: 'name',
					content: formatMessage(messages.name),
					isSortable: true,
				},
				{
					key: 'last-pinged',
					content: formatMessage(messages.lastPinged),
					isSortable: true,
				},
				{
					key: 'next-ping',
					content: formatMessage(messages.nextPing),
					isSortable: true,
				},
				...(!teamId && fg('global_heartbeats')
					? [
							{
								key: 'owner-team',
								content: formatMessage(messages.teams),
							},
						]
					: []),
				{
					key: 'status',
					content: formatMessage(messages.status),
				},
				{
					key: 'more',
					content: formatMessage(messages.actions),
				},
			],
		}),
		[formatMessage, teamId],
	);

	const rows = useMemo(() => {
		return heartbeats.map((heartbeat, index) => {
			const { name, lastReceivedAt, willExpireAt, status, ownerTeamId } = heartbeat;

			return {
				key: `row-${index}-${name}`,
				onClick: () => focusState.open({ data: heartbeat, mode: 'view' }),
				cells: [
					{
						key: `cell-${index}-${name}`,
						content: name,
					},
					{
						key: `cell-${index}-${lastReceivedAt}`,
						content:
							MIN_DATE_SHOWN_EPOCH <= lastReceivedAt && lastReceivedAt <= getMaxAvailableEpoch()
								? formatInTimeZone(new Date(lastReceivedAt), timezone, 'MMM d h:mm a')
								: '-',
					},
					{
						key: `cell-${index}-${willExpireAt}`,
						content:
							MIN_DATE_SHOWN_EPOCH <= willExpireAt && willExpireAt <= getMaxAvailableEpoch()
								? formatInTimeZone(new Date(willExpireAt), timezone, 'MMM d h:mm a')
								: '-',
					},
					...(!teamId && fg('global_heartbeats')
						? [
								{
									key: `cell-${index}-${ownerTeamId}`,
									content: <Team ownerTeamId={ownerTeamId} />,
								},
							]
						: []),
					{
						key: `cell-${index}-${status}`,
						content: <HeartbeatStatus heartbeat={heartbeat} isTransitioning={isTransitioning} />,
					},
					{
						key: `cell-${index}-more`,
						content:
							index === 0 ? (
								<SpotlightTarget name={SPOTLIGHT_NAMES.actions}>
									<Action
										heartbeat={heartbeat}
										onDeleteClick={() => handleDeleteClick(heartbeat)}
										onSwitchClick={() => handleSwitchClick(heartbeat)}
									/>
								</SpotlightTarget>
							) : (
								<Action
									heartbeat={heartbeat}
									onDeleteClick={() => handleDeleteClick(heartbeat)}
									onSwitchClick={() => handleSwitchClick(heartbeat)}
								/>
							),
					},
				],
			};
		});
	}, [
		focusState,
		handleDeleteClick,
		handleSwitchClick,
		heartbeats,
		isTransitioning,
		teamId,
		timezone,
	]);

	if (rows.length === 0) {
		return <NoSearchResultEmptyState />;
	}

	return (
		<DynamicTableWrapper>
			<DynamicTable head={head} rows={rows} isFixedSize />
			<DeleteModal
				onDeleteAttemptCancel={handleDeleteModalClose}
				onDelete={handleDelete}
				isLoading={isDeleting}
				isDeleteDialogOpen={isDeleteModalOpen}
			/>
			<SwitchModal
				onSwitchAttemptCancel={handleSwitchModalClose}
				onSwitch={handleSwitch}
				isLoading={isSwitchLoading}
				isSwitchDialogOpen={isSwitchModalOpen}
			/>
		</DynamicTableWrapper>
	);
};
